import type { Opportunity } from './types'
import { sortOpps, urgencyRank } from './format'

/**
 * The board report — a self-contained HTML file the user downloads and prints to PDF.
 *
 * Ported from the prototype's generateReport() near-verbatim: same cover page, same tables,
 * same inline styles (inline because the file must carry its own styling once it leaves the
 * app). It stays client-side on purpose — it renders data the dashboard already holds, no
 * server round-trip or Claude call involved.
 */

const esc = (v: string | null | undefined): string =>
  String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const bandBadge = (n: Opportunity): string =>
  n.band === 'HOT'
    ? '<span style="font-size:9px;font-weight:800;padding:3px 7px;border-radius:99px;background:#fdeef1;color:#EF5974">HOT</span>'
    : '<span style="font-size:9px;font-weight:800;padding:3px 7px;border-radius:99px;background:#e6f0fb;color:#0753AD">WARM</span>'

const dlBadge = (n: Opportunity): string => {
  const dl = n.deadline || 'TBC'
  const u = n.deadlineUrgency || 'watch'
  if (!dl || ['TBC', '-', 'Submitted'].includes(dl))
    return '<span style="font-size:9px;font-weight:700;padding:2px 7px;border-radius:99px;background:#f0f0f5;color:#808080">PIN/TBC</span>'
  if (u === 'urgent')
    return `<span style="font-size:9px;font-weight:700;padding:2px 7px;border-radius:99px;background:#fdeef1;color:#EF5974">⚡ ${esc(dl)}</span>`
  if (u === 'soon')
    return `<span style="font-size:9px;font-weight:700;padding:2px 7px;border-radius:99px;background:#fff3e0;color:#d97706">${esc(dl)}</span>`
  return `<span style="font-size:9px;font-weight:700;padding:2px 7px;border-radius:99px;background:#f0f0f5;color:#808080">${esc(dl)}</span>`
}

const caps = (n: Opportunity): string =>
  (n.capabilities || [])
    .map(
      (c) =>
        `<span style="font-size:9px;font-weight:600;padding:1px 6px;border-radius:99px;background:#f3eaf6;color:#8C4189">${esc(c)}</span>`,
    )
    .join(' ')

const row = (n: Opportunity, i: number): string => `<tr>
  <td style="color:#808080;font-weight:700;text-align:center;padding:7px 4px">${i}</td>
  <td style="padding:7px 6px">${bandBadge(n)}</td>
  <td style="font-weight:900;font-size:12px;text-align:center;padding:7px 4px">${n.fitScore ?? ''}/10</td>
  <td style="padding:7px 6px">
    <div style="font-weight:700;font-size:11px;color:#06325E">${esc(n.title)}</div>
    <div style="color:#808080;font-size:10px">${esc(n.buyer)}</div>
    <div style="margin-top:4px">${caps(n)}</div>
  </td>
  <td style="padding:7px 6px;font-size:10px;white-space:nowrap">${esc(n.value || 'TBC')}</td>
  <td style="padding:7px 6px">${dlBadge(n)}</td>
  <td style="padding:7px 6px;font-size:10px;color:#555566;max-width:200px">${esc(n.why)}</td>
  <td style="padding:7px 6px;font-size:10px;max-width:160px"><strong>${esc(n.action)}</strong><br><span style="color:#808080;font-size:9px">${esc(n.actionDetail)}</span></td>
  <td style="padding:7px 6px;min-width:80px;border-left:2px dashed #e0e0e8"></td>
</tr>`

const thStyle =
  'background:#f3eaf6;color:#06325E;font-weight:800;text-transform:uppercase;letter-spacing:.4px;padding:7px 6px;text-align:left;border-bottom:2px solid #8C4189;font-size:9px;white-space:nowrap'

const tableHead = `<thead><tr>
  <th style="${thStyle}">#</th>
  <th style="${thStyle}">Band</th>
  <th style="${thStyle}">Score</th>
  <th style="${thStyle}">Opportunity</th>
  <th style="${thStyle}">Value</th>
  <th style="${thStyle}">Deadline</th>
  <th style="${thStyle}">Why it fits</th>
  <th style="${thStyle}">Next action</th>
  <th style="${thStyle}">Decision</th>
</tr></thead>`

/** Builds the report from the full opportunity list and triggers the download. */
export function generateReport(all: Opportunity[]): void {
  const hot = sortOpps(all.filter((n) => n.band === 'HOT'))
  const warm = sortOpps(all.filter((n) => n.band === 'WARM'))
  const urgent = all.filter((n) => urgencyRank(n.deadlineUrgency) === 0).length
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

  const report = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
  <title>Burendo Opportunity Report ${today}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;color:#06325E;background:#fff;font-size:11px}
    table{width:100%;border-collapse:collapse}
    tr:nth-child(even) td{background:#fafafa}
    @media print{
      .cover{-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .thead{-webkit-print-color-adjust:exact;print-color-adjust:exact}
      tr{page-break-inside:avoid}
    }
  </style>
  </head><body>

  <div class="cover" style="background:#06325E;color:#fff;padding:48px;page-break-after:always">
    <div style="font-size:24px;font-weight:900;margin-bottom:4px">Burendo <span style="color:#E1539D">OIP</span></div>
    <div style="font-size:12px;opacity:.7;margin-bottom:40px">Opportunity Intelligence Platform</div>
    <div style="font-size:32px;font-weight:900;line-height:1.2;margin-bottom:10px">Business Development<br>Opportunity Report</div>
    <div style="font-size:14px;opacity:.8;margin-bottom:36px">Go/No-Go Review — ${today}</div>
    <div style="display:flex;gap:24px">
      <div style="background:rgba(255,255,255,.1);border-radius:8px;padding:14px 20px;text-align:center">
        <div style="font-size:28px;font-weight:900">${hot.length + warm.length}</div>
        <div style="font-size:10px;opacity:.7;text-transform:uppercase">Total</div>
      </div>
      <div style="background:rgba(255,255,255,.1);border-radius:8px;padding:14px 20px;text-align:center">
        <div style="font-size:28px;font-weight:900;color:#EF5974">${hot.length}</div>
        <div style="font-size:10px;opacity:.7;text-transform:uppercase">HOT</div>
      </div>
      <div style="background:rgba(255,255,255,.1);border-radius:8px;padding:14px 20px;text-align:center">
        <div style="font-size:28px;font-weight:900;color:#28A0DD">${warm.length}</div>
        <div style="font-size:10px;opacity:.7;text-transform:uppercase">WARM</div>
      </div>
      <div style="background:rgba(255,255,255,.1);border-radius:8px;padding:14px 20px;text-align:center">
        <div style="font-size:28px;font-weight:900;color:#E1539D">${urgent}</div>
        <div style="font-size:10px;opacity:.7;text-transform:uppercase">Act This Week</div>
      </div>
    </div>
    <div style="margin-top:24px;font-size:10px;opacity:.4">Prepared ${today} · Commercial in Confidence</div>
  </div>

  <div style="padding:32px 48px">
    <div style="font-size:16px;font-weight:900;border-bottom:3px solid #EF5974;padding-bottom:8px;margin-bottom:20px">HOT Opportunities — Pursue (${hot.length})</div>
    <table>${tableHead}<tbody>${hot.map((n, i) => row(n, i + 1)).join('')}</tbody></table>
  </div>

  <div style="padding:32px 48px">
    <div style="font-size:16px;font-weight:900;border-bottom:3px solid #0753AD;padding-bottom:8px;margin-bottom:20px">WARM Opportunities — Watch (${warm.length})</div>
    <table>${tableHead}<tbody>${warm.map((n, i) => row(n, i + 1)).join('')}</tbody></table>
  </div>

  <div style="padding:16px 48px;border-top:2px solid #e0e0e8;display:flex;justify-content:space-between;color:#808080;font-size:10px">
    <div style="font-weight:900;color:#06325E;font-size:12px">Burendo <span style="color:#E1539D">OIP</span></div>
    <div>Prepared ${today} · Commercial in Confidence</div>
  </div>
  </body></html>`

  const blob = new Blob([report], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'Burendo_Opportunities_' + new Date().toISOString().slice(0, 10) + '.html'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
